import React from 'react';
import { connect, useSelector } from 'react-redux';
import { Box, Breadcrumb, Rating, TextLink, Typography } from '@drone-js/core';
import { DetailsCardProps, mapDispatchToProps, mapStateToProps } from 'component/page/detail/DetailCard/DetailsCard.selectors';
import { StringFormatter } from '@agoda/react-handyman';
import ActivityCardBadge from 'component/common/Badge/ActivityCardBadge';
import BookingCounter from 'component/common/BookingCounter/BookingCounter';
import { ActivityBadgeCategory, ActivityBadgeCategoryProps } from 'component/common/Badge/BadgeService.types';
import Feature from 'component/common/Experiment/Feature';
import { FeatureName } from 'core/constant/FeatureName';
import { generateBreadCrumbsForDetailsPage } from 'core/utils/breadCrumbsGenerator';
import { selectFeatureBehaviorFromBootstrap } from 'store/slice/bootstrap';
import TypographyWithWordBreak from 'component/common/TypographyWithWordBreak/TypographyWithWordBreak';

const categoryProps: ActivityBadgeCategoryProps = {
    maxBadges: { s: 5, m: 5, l: 5 },
    showSummary: { s: false, m: false, l: false },
    boxMargin: {
        s: { mt: '4', me: '4' },
        m: { mt: '4', me: '4' },
        l: { mt: '4', me: '4' },
    },
    badgeMargin: {
        s: { mb: '4', me: '4' },
        m: { mb: '4', me: '4' },
        l: { mb: '4', me: '4' },
    },
};

const DetailCard: React.FC<DetailsCardProps> = (props): React.JSX.Element => {
    const isPageContainerSpacing = useSelector(selectFeatureBehaviorFromBootstrap).PageContainerSpacing;
    const reviewsAvailable = !!props.reviewAverageScore && !!props.reviewTotalReview;
    const bookInfoBadges = props.badges ? props.badges.bookingInfo || [] : [];
    const highlightBadges = props.badges ? props.badges.activityHighlights || [] : [];
    const breadcrumbs = generateBreadCrumbsForDetailsPage(props.cms, props.activityId, props.categoryId, props.title);
    const areBreadCrumbsValid = breadcrumbs && breadcrumbs.length > 0;
    const reviewScore = Number(props.reviewAverageScore);

    return (
        <Box data-testid="details-card" py="0">
            {props.screenSize.desktop && areBreadCrumbsValid && (
                <Box mb="16" mt={isPageContainerSpacing ? undefined : '16'}>
                    <Breadcrumb data-testid="details-breadcrumbs" contents={breadcrumbs} />
                </Box>
            )}
            <Feature.On name={FeatureName.BadgesV2DetailsPage}>
                <Box display="flex" mt={isPageContainerSpacing ? undefined : '8'}>
                    <ActivityCardBadge
                        badges={highlightBadges}
                        activityId={props.activityId}
                        badgeCategory={ActivityBadgeCategory.HIGHLIGHT}
                        isBVariant={true}
                        overrideCategoryProps={categoryProps}
                    />
                </Box>
            </Feature.On>
            <Feature.On name={FeatureName.WordBreakOnDetailPage}>
                <TypographyWithWordBreak
                    data-testid="details-title"
                    variant="heading-default"
                    tag="h1"
                    textAlign="start"
                    mt={isPageContainerSpacing ? undefined : '12'}
                    mb="0"
                    lang={props.contentLocale}
                >
                    {props.title}
                </TypographyWithWordBreak>
            </Feature.On>
            <Feature.Off name={FeatureName.WordBreakOnDetailPage}>
                <Typography
                    data-testid="details-title"
                    variant="heading-default"
                    tag="h1"
                    textAlign="start"
                    mt={isPageContainerSpacing ? undefined : '12'}
                    mb="0"
                    lang={props.contentLocale}
                >
                    {props.title}
                </Typography>
            </Feature.Off>
            <Box display="flex" flexDirection="row" alignItems="center" my="8">
                {reviewsAvailable && (
                    <Box data-testid="activities-details-review-summary" display="inline-flex" flexDirection="row" alignItems="center">
                        <Box display="inline-flex">
                            {/* eslint-disable-next-line @agoda/agoda-a11y/no-hardcoded-content -- @agoda/agoda-a11y/no-hardcoded-content[f4cb93eb-362a-45fd-b730-e874ae0c52e8] */}
                            <Rating data-testid="review-star-rating" label="review-star-rating" score={reviewScore} size="m" variant="star" color="hotel" me="4" />
                        </Box>
                        <Typography data-testid="review-score" variant="heading-default" textColor="product.rating-hotels" pe="4">
                            {props.reviewAverageScore}
                        </Typography>
                        <TextLink
                            data-element-name="activities-see-review-textlink"
                            data-testid="activities-see-review-textlink"
                            label={`${StringFormatter.format(props.cms[168191], props.reviewTotalReview.toLocaleString(props.contentLocale))}`}
                            onClick={() => props.onSeeReviewClick(props.seeReviewHref)}
                            textVariant="body-s"
                        />
                    </Box>
                )}
                <Feature.On name={FeatureName.BadgesV2DetailsPage}>
                    <Box display="inline-flex" flexDirection="row">
                        <BookingCounter showSeparator={!!props.reviewAverageScore && !!props.reviewTotalReview} badges={bookInfoBadges} />
                    </Box>
                </Feature.On>
            </Box>
        </Box>
    );
};
DetailCard.displayName = 'DetailCard';

export default connect(mapStateToProps, mapDispatchToProps)(DetailCard);
